import {
  ButtonSize,
  Typography,
  ButtonVariant,
  SecondaryButton,
} from '@uni-of-exeter/digital.lib.react.ui.kit';

import './recommendationsV2.css';
import { APP_TEXT } from '../../const/app-text';
import { useBoolean } from '../../hooks/utils/use-boolean';
import { ShareToManagerModal } from '../modals/share-to-manager';
import { ResourceFilter, ResourceFilterProps } from '../filters/resource-filter';
import { RecommendationsCard, RecommendationsGroup } from './recommendations-card';
import DigitalSkillsTopicFilter, { FilterTopicType } from '../filters/digital-skills-topic-filter';

const pageText = APP_TEXT.pages.recommendation;

export interface RecommendationsV2Props {
  title: string;
  completedOn: string;
  groups: RecommendationsGroup[];
  filterTopics: FilterTopicType[];
  selectedTopics: FilterTopicType[];
  onTopicChange: (topic: FilterTopicType) => void;
  filters: ResourceFilterProps;
  managerEmail: string;
  userFullName: string;
  userEmail: string;
  showShareToManager: boolean;
}

export const RecommendationsV2 = ({
  title,
  completedOn,
  groups,
  filterTopics,
  selectedTopics,
  onTopicChange,
  filters,
  managerEmail,
  userFullName,
  userEmail,
  showShareToManager,
}: RecommendationsV2Props) => {
  const shareToManagerDialog = useBoolean(false);

  return (
    <section
      className="recommendationsComponentV2"
      aria-label={title}
    >
      <header className="recommendationsComponentHeader">
        <h2 className="recommendationsComponentTitle">{title}</h2>

        <div className="recommendationsComponentSubHeader">
          <p className="recommendationsComponentIntro">
            {pageText.subTitle} <strong>{completedOn}</strong>
          </p>
        </div>
      </header>

      <div className="recommendationsFilters">
        <ResourceFilter
          filters={filters.filters}
          skillOptions={filters.skillOptions}
          mediaTypeOptions={filters.mediaTypeOptions}
          locationOptions={filters.locationOptions}
          updateFilters={filters.updateFilters}
          hasActiveFilters={filters.hasActiveFilters}
          resetFilters={filters.resetFilters}
          searchPlaceholder={filters.searchPlaceholder}
        />
      </div>
      <div className="recommendationsFilterTopics">
        <DigitalSkillsTopicFilter
          label={pageText.filters.topicFilterTitle}
          values={filterTopics}
          selectedValues={selectedTopics}
          showMoreText={pageText.filters.topicFilterShowMore}
          showLessText={pageText.filters.topicFilterShowLess}
          onChange={onTopicChange}
        />
      </div>

      <div className="recommendationsComponentCards">
        {groups.filter((group) => group.resources.length > 0).length === 0 && (
          <div className="recommendationsNoResults">
            <Typography
              variant="body.small"
              fontWeight={500}
            >
              {pageText.noResults}
            </Typography>
          </div>
        )}
        {groups.map((group) => (
          <RecommendationsCard
            key={group.id}
            group={group}
          />
        ))}
      </div>

      {showShareToManager && (
        <div className="sharingYourRecommendations">
          <img
            className="sharingYourRecommendationsIcon"
            src={`/assets/${pageText.sharingYourRecommendations.icon.url}`}
            alt={pageText.sharingYourRecommendations.icon.alt}
          />
          <div className="column">
            <div>{pageText.sharingYourRecommendations.title}</div>
            <Typography variant="body.small">{pageText.sharingYourRecommendations.body}</Typography>
          </div>

          <div className="shareWithManager">
            <SecondaryButton
              variant={ButtonVariant.Solid}
              aria-label={pageText.sharingYourRecommendations.composeEmail.buttonLabel}
              label={pageText.sharingYourRecommendations.composeEmail.buttonLabel}
              className="shareWithManagerButton"
              buttonSize={ButtonSize.Small}
              onClick={shareToManagerDialog.onTrue}
            />
            <ShareToManagerModal
              isOpen={shareToManagerDialog.value}
              onClose={shareToManagerDialog.onFalse}
              learningRecommendations={groups.flatMap((group) =>
                group.resources.map((resource) => ({
                  id: resource.id,
                  title: resource.title,
                }))
              )}
              managerEmail={managerEmail}
              currentUserFullName={userFullName}
              currentUserEmail={userEmail}
            />
          </div>
        </div>
      )}
    </section>
  );
};
